import React, { useMemo, useState } from 'react';
import { css } from '@emotion/css';
import {
  Theme,
  useTheme,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Box,
} from '@mui/material';
import { BiSearch } from 'react-icons/bi';
import { Customers } from '@gql/base-types';
import {
  isSuperAdmin as checkIsSuperAdmin,
  ROLE_DEALER,
  ROLE_DEALER_ADMIN,
  ROLE_DEALER_SUPER_ADMIN,
} from '../../utils/roleUtils';
import TierChip from '@features/rooftops/components/TierChip';

// -- Types --

export interface RooftopData {
  rooftopIds: number[];
  isSuperAdmin: boolean;
}

interface RooftopStepProps {
  data: RooftopData;
  onChange: (data: RooftopData) => void;
  availableCustomers: Customers[];
  currentUserRole?: string;
  hideSuperAdmin?: boolean;
}

interface CustomerRow {
  id: number;
  name: string;
  tier: string | null;
}

type SortKey = 'name' | 'tier' | 'access';
type SortOrder = 'asc' | 'desc';

// -- Constants --

// -- Helpers --

export function deriveRole(data: RooftopData): string {
  if (data.isSuperAdmin) return ROLE_DEALER_SUPER_ADMIN;
  if (data.rooftopIds.length >= 2) return ROLE_DEALER_ADMIN;
  return ROLE_DEALER;
}

// -- Component --

const RooftopStep: React.FC<RooftopStepProps> = ({ data, onChange, availableCustomers, currentUserRole, hideSuperAdmin }) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const showSuperAdminToggle = !hideSuperAdmin && currentUserRole && checkIsSuperAdmin(currentUserRole);
  const selectedIds = useMemo(() => new Set(data.rooftopIds), [data.rooftopIds]);

  // Table state
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState<SortKey>('name');
  const [order, setOrder] = useState<SortOrder>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Build rows
  const allRows: CustomerRow[] = useMemo(
    () =>
      availableCustomers.map((c: any) => ({
        id: c.id,
        name: c.name ?? `Customer #${c.id}`,
        // Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
        tier: c.tier ?? null,
      })),
    [availableCustomers],
  );

  // Filter, sort, paginate
  const filteredRows = useMemo(() => {
    let rows = [...allRows];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(c => c.name.toLowerCase().includes(q));
    }

    rows.sort((a, b) => {
      let aVal: any;
      let bVal: any;
      if (orderBy === 'access') {
        aVal = selectedIds.has(a.id) ? 1 : 0;
        bVal = selectedIds.has(b.id) ? 1 : 0;
      } else {
        aVal = (a as any)[orderBy] ?? '';
        bVal = (b as any)[orderBy] ?? '';
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [allRows, search, orderBy, order, selectedIds]);

  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredRows, page, rowsPerPage],
  );

  // Handlers
  const handleSort = (property: SortKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleToggleSuperAdmin = (_: React.ChangeEvent, checked: boolean) => {
    onChange({
      isSuperAdmin: checked,
      rooftopIds: checked ? availableCustomers.map(c => c.id) : [],
    });
  };

  const handleToggleMarket = (customerId: number, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) {
      next.add(customerId);
    } else {
      next.delete(customerId);
    }
    onChange({ ...data, rooftopIds: Array.from(next) });
  };

  return (
    <div>
      <Typography variant="overline" fontWeight={600} color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Rooftop Access
      </Typography>

      {showSuperAdminToggle && (
        <>
          <div className={cls.superAdminToggle}>
            <FormControlLabel
              control={
                <Switch checked={data.isSuperAdmin} onChange={handleToggleSuperAdmin} color="primary" />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  Grant Super Admin access
                </Typography>
              }
            />
            <Typography variant="caption" color="text.secondary">
              Super Admins automatically have access to all rooftops in their organization.
            </Typography>
          </div>

          <div className={cls.spacer} />
        </>
      )}

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search rooftops..."
        value={search}
        onChange={e => {
          setSearch(e.target.value);
          setPage(0);
        }}
        fullWidth
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <BiSearch size={18} color="#778088" />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: 13, height: 36 } }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {selectedIds.size} of {allRows.length} selected
        </Typography>
      </Box>

      {/* Table */}
      {allRows.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No rooftops available.</Typography>
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No rooftops match your search.</Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 160 }}>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ width: 100 }}>
                    <TableSortLabel
                      active={orderBy === 'tier'}
                      direction={orderBy === 'tier' ? order : 'asc'}
                      onClick={() => handleSort('tier')}
                    >
                      Tier
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ width: 80 }}>
                    <TableSortLabel
                      active={orderBy === 'access'}
                      direction={orderBy === 'access' ? order : 'asc'}
                      onClick={() => handleSort('access')}
                    >
                      Access
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map(customer => {
                  const hasAccess = selectedIds.has(customer.id);
                  return (
                    <TableRow key={customer.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{customer.name}</Typography>
                      </TableCell>
                      <TableCell><TierChip tier={customer.tier} /></TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={data.isSuperAdmin || hasAccess}
                          disabled={data.isSuperAdmin}
                          onChange={(_e, checked) => handleToggleMarket(customer.id, checked)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredRows.length > 10 && (
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          )}
        </>
      )}
    </div>
  );
};

// -- Styles --

const useStyles = (theme?: Theme) => ({
  spacer: css`
    width: 100%;
    height: 12px;
  `,
  superAdminToggle: css`
    padding: 12px;
    border: 1px solid ${theme?.palette?.divider};
    border-radius: 10px;
    background-color: ${theme?.palette?.action?.hover};
  `,
});

export default RooftopStep;
